import { Controller } from '@hotwired/stimulus';
import { loadStripe } from '@stripe/stripe-js';

// Mirrors the tailwind theme (tailwind.config.js) inside the Stripe iframe
const APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#050960',
    colorText: '#090C2B',
    colorDanger: '#AD2923',
    borderRadius: '4px',
  },
  rules: {
    '.Label': { fontSize: '14px', fontWeight: '500', color: '#090C2B' },
    '.Input': { border: '1px solid #bdbfff', boxShadow: 'none' },
    '.Input:focus': { borderColor: '#050960', boxShadow: '0 0 0 3px rgba(62, 81, 255, 0.5)' },
  },
};

export default class extends Controller {
  static targets = ['paymentElement', 'holderName', 'error', 'errorMessage', 'submitButton'];
  static values = {
    publishableKey: String,
    clientSecret: String,
    customerSessionClientSecret: String,
    locale: String,
    returnUrl: String,
    genericError: String,
    unexpectedError: String,
  };

  async connect() {
    try {
      this.stripe = await loadStripe(this.publishableKeyValue, { locale: this.localeValue });
    } catch (error) {
      this.#showUnexpectedError('Stripe.js load error', error);
      return;
    }

    this.elements = this.stripe.elements({
      clientSecret: this.clientSecretValue,
      customerSessionClientSecret: this.customerSessionClientSecretValue,
      appearance: APPEARANCE,
    });
    const paymentElement = this.elements.create('payment', { wallets: { link: 'never' } });
    paymentElement.on('loaderror', (event) => {
      this.#showUnexpectedError('Payment Element load error', event.error);
    });
    paymentElement.mount(this.paymentElementTarget);
  }

  async pay() {
    this.#hideError();
    this.submitButtonTarget.disabled = true;

    try {
      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: this.returnUrlValue,
          payment_method_data: { billing_details: { name: this.holderNameTarget.value } },
        },
        redirect: 'if_required',
      });

      if (error?.type === 'card_error' || error?.type === 'validation_error') {
        this.#showError(error.message || this.genericErrorValue);
      } else if (error) {
        this.#showUnexpectedError('payment confirmation error', error);
      } else {
        window.location.assign(this.returnUrlValue);
      }
    } catch (error) {
      this.#showUnexpectedError('payment confirmation error', error);
    }
  }

  #showUnexpectedError(context, error) {
    console.error(`[Stripe] ${context}`, error);
    this.#showError(this.unexpectedErrorValue);
  }

  #showError(message) {
    this.errorMessageTarget.textContent = message;
    this.errorTarget.classList.remove('hidden');
    this.submitButtonTarget.disabled = false;
  }

  #hideError() {
    this.errorMessageTarget.textContent = '';
    this.errorTarget.classList.add('hidden');
  }
}
